import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { SetService } from '@app/core';
import { MarketEntity } from '@games/entities';
import { IMarketEntity } from '@games/interfaces';

@Injectable({
  providedIn: 'root',
})
export class MarketEntityService {
  constructor(
    private http: HttpClient,
    private setService: SetService
  ) {}

  createEntity(
    courseId: number,
    gameId: number,
    marketId: number,
    name: string
  ): Observable<MarketEntity> {
    return this.http
      .post<any>(
        `${environment.apiUrl}courses/${courseId}/games/${gameId}/groupings/${marketId}/entities/`,
        { name }
      )
      .pipe(map((entity: IMarketEntity) => new MarketEntity(entity)));
  }

  updateEntity(
    courseId: number,
    gameId: number,
    marketId: number,
    id: number,
    name: string
  ): Observable<MarketEntity> {
    return this.http
      .patch<any>(
        `${environment.apiUrl}courses/${courseId}/games/${gameId}/groupings/${marketId}/entities/${id}/`,
        { name }
      )
      .pipe(map((entity: IMarketEntity) => new MarketEntity(entity)));
  }

  deleteEntity(courseId: number, gameId: number, marketId: number, id: number) {
    return this.http.delete<any>(
      `${environment.apiUrl}courses/${courseId}/games/${gameId}/groupings/${marketId}/entities/${id}/`,
      { observe: 'response' }
    );
  }

  addPlayers(
    courseId: number,
    gameId: number,
    marketId: number,
    id: number,
    userIds: number[]
  ): Observable<MarketEntity> {
    return this.http
      .patch<any>(
        `${environment.apiUrl}courses/${courseId}/games/${gameId}/groupings/${marketId}/entities/${id}/users/`,
        { userIds }
      )
      .pipe(map((entity: IMarketEntity) => new MarketEntity(entity)));
  }

  createCompleteEntity(courseId: number, gameId: number, marketId: number, entity: any) {
    return this.createEntity(courseId, gameId, marketId, entity.name).pipe(
      switchMap(e => this.addPlayers(courseId, gameId, marketId, e.id, entity.players))
    );
  }

  updateCompleteEntity(
    courseId: number,
    gameId: number,
    marketId: number,
    entity: any,
    existingEntity: any
  ) {
    if (existingEntity.name !== entity.name) {
      return this.updateEntity(courseId, gameId, marketId, entity.id, entity.name).pipe(
        filter(e => this.haveDifferentPlayers(entity, e)),
        switchMap(e => this.addPlayers(courseId, gameId, marketId, entity.id, entity.players))
      );
    }

    if (this.haveDifferentPlayers(entity, existingEntity)) {
      return this.addPlayers(courseId, gameId, marketId, entity.id, entity.players);
    }
  }

  haveDifferentPlayers(entity, existingEntity) {
    return (
      !!this.setService.getDifference(entity.players, existingEntity.players, '', 'id').length ||
      !!this.setService.getDifference(existingEntity.players, entity.players, 'id', '').length
    );
  }
}
